/*#
# The sync log

The seam between virta and its backend is one small interface: **pull events
after a cursor, push new events**. The log assigns `seq`; writers never do.

`MemoryLog` is the in-process implementation, used by tests, seeds, and live
examples. [`IndexedDBLog`](/indexeddb-log/) persists in the browser. The
service-compris adapter will implement the same interface against the remote
endpoint — the client-side cache stays exactly as it is and doubles as the
read-only degradation copy.

The guard that matters: the log is **append-only and shaped as a log**, never
a document store with a tasks table the UI reads and writes directly. If it
were, the board would be built against the wrong contract.
*/

/*{ "parent": "Reference", "order": 40 }*/

import type { EventDraft, VirtaEvent } from './model'

export interface LogPage {
  events: VirtaEvent[]
  /** `seq` of the last event in the page, or the cursor you asked from if empty. */
  cursor: number
  /**
   * Whether the log holds events beyond this page. A log that caps its page
   * size below what was asked (a server will) must say so; when absent, a
   * page shorter than the limit is taken to be the last.
   */
  more?: boolean
}

export interface SyncLog {
  /** Events with `seq > after`, in order, at most `limit`. */
  pull(after: number, limit?: number): Promise<LogPage>
  /**
   * Append in order; returns the same events with `seq` assigned. Idempotent
   * by event id: a draft whose id the log already holds is not appended and
   * the stored event is returned in its place — so a retried push, or two
   * clients seeding the same log, cannot duplicate.
   */
  push(drafts: EventDraft[]): Promise<VirtaEvent[]>
}

export const DEFAULT_PAGE = 1000

/** In-memory log. Deterministic, synchronous underneath, async-shaped like the rest. */
export class MemoryLog implements SyncLog {
  events: VirtaEvent[] = []

  async pull(after: number, limit = DEFAULT_PAGE): Promise<LogPage> {
    // seq is ascending; dense from 1 when this log assigned it, a host's
    // numbering when it is a replica (see `append`) — so search, not slice.
    let lo = 0
    let hi = this.events.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (this.events[mid]!.seq <= after) lo = mid + 1
      else hi = mid
    }
    const events = this.events.slice(lo, lo + limit)
    const last = events[events.length - 1]
    return { events, cursor: last ? last.seq : after }
  }

  private byId = new Map<string, VirtaEvent>()

  /** As a replica: store events with the sequence numbers they arrived with. */
  async append(events: VirtaEvent[]): Promise<void> {
    for (const event of events) {
      if (this.byId.has(event.id)) continue
      const tail = this.events[this.events.length - 1]?.seq ?? 0
      if (event.seq <= tail)
        throw new Error(
          `virta: cannot append seq ${event.seq} after ${tail} (out of order)`
        )
      this.events.push(event)
      this.byId.set(event.id, event)
    }
  }

  async tail(): Promise<number> {
    return this.events[this.events.length - 1]?.seq ?? 0
  }

  /** Idempotent by id: pushing an event the log already holds returns the stored one. */
  async push(drafts: EventDraft[]): Promise<VirtaEvent[]> {
    const out: VirtaEvent[] = []
    for (const draft of drafts) {
      const existing = this.byId.get(draft.id)
      if (existing) {
        out.push(existing)
        continue
      }
      const tail = this.events[this.events.length - 1]?.seq ?? 0
      const event = { ...draft, seq: tail + 1 } as VirtaEvent
      this.events.push(event)
      this.byId.set(event.id, event)
      out.push(event)
    }
    return out
  }
}

/** Pull every page after `cursor`, calling `onPage` for each. Returns the final cursor. */
export async function replay(
  log: SyncLog,
  cursor: number,
  onPage: (events: VirtaEvent[]) => void,
  limit = DEFAULT_PAGE
): Promise<number> {
  for (;;) {
    const page = await log.pull(cursor, limit)
    if (page.events.length === 0) return cursor
    onPage(page.events)
    cursor = page.cursor
    const last =
      page.more === undefined ? page.events.length < limit : !page.more
    if (last) return cursor
  }
}
